import React, { FC, useEffect, useState } from 'react';
import { editPacuNote, editPreOpNote } from '@/graph/patients';
import { useMutation } from '@apollo/client';
import { Note, Patient } from '@/types/Patient';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

type PreOpNoteInputProps = {
  open: boolean;
  handleClose: () => void;
  patient?: Patient;
  onClick?: (note: Note) => void | Promise<void>;
};

const PreOpNoteInput: FC<PreOpNoteInputProps> = ({ open, handleClose, patient, onClick }) => {
  const [note, setNote] = useState<string>(patient?.preOpNote ?? '');
  const [saveForPacu, setSaveForPacu] = useState<boolean>(!!patient?.pacuNote);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [edit] = useMutation(editPreOpNote);
  const [editPacu] = useMutation(editPacuNote);

  useEffect(() => {
    if (!open) {
      return;
    }
    setNote(patient?.preOpNote ?? '');
    setSaveForPacu(!!patient?.pacuNote);
  }, [open, patient?.id, patient?.preOpNote, patient?.pacuNote]);

  const onSave = async () => {
    if (isSaving) {
      return;
    }

    const normalizedOriginalPreOpNote = patient?.preOpNote ?? '';
    const normalizedOriginalPacuNote = patient?.pacuNote ?? '';
    const targetPacuNote: Note = saveForPacu ? note : undefined;
    const normalizedTargetPacuNote = saveForPacu ? note : '';
    const shouldUpdatePreOp = note !== normalizedOriginalPreOpNote;
    const canUpdatePacu = patient?.id != null;
    const shouldUpdatePacu = canUpdatePacu && normalizedTargetPacuNote !== normalizedOriginalPacuNote;

    setIsSaving(true);

    try {
      if (onClick) {
        if (shouldUpdatePacu && patient?.id) {
          await editPacu({
            variables: { id: patient.id, note: targetPacuNote },
            update: cache => {
              cache.modify({
                id: cache.identify({ __typename: 'Patient', id: patient.id }),
                fields: {
                  pacuNote: () => targetPacuNote,
                },
              });
            },
          });
        }
        await onClick(note);
      } else {
        if (shouldUpdatePreOp && patient?.id) {
          await edit({
            variables: { id: patient.id, note },
            update: cache => {
              cache.modify({
                id: cache.identify({ __typename: 'Patient', id: patient.id }),
                fields: {
                  preOpNote: () => note,
                },
              });
            },
          });
        }
        if (canUpdatePacu && shouldUpdatePacu && patient?.id) {
          await editPacu({
            variables: { id: patient.id, note: targetPacuNote },
            update: cache => {
              cache.modify({
                id: cache.identify({ __typename: 'Patient', id: patient.id }),
                fields: {
                  pacuNote: () => targetPacuNote,
                },
              });
            },
          });
        }
      }
      handleClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle id="form-dialog-title">Write PreOp Note</DialogTitle>
      <DialogContent>
        <Box mt={4} display="flex" flexDirection="column">
          <Typography gutterBottom>PreOp Note</Typography>
          <Box mb={2}>
            <TextField
              fullWidth
              label="Note"
              variant="filled"
              multiline
              minRows={1}
              onChange={e => setNote(e.target.value)}
              value={note}
            />
          </Box>
          <FormControlLabel
            label="Note follows Patient"
            control={<Checkbox checked={saveForPacu} onChange={e => setSaveForPacu(e.target.checked)} />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button color="secondary" onClick={onSave} disabled={isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreOpNoteInput;
